import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Space, Typography, Select, Table, Spin, Empty, message, Row, Col } from 'antd';
import { DownloadOutlined, PrinterOutlined, ShareAltOutlined, ReloadOutlined, BarChartOutlined, TableOutlined } from '@ant-design/icons';
import DataVisualization from '../components/common/DataVisualization';
import ChartConfigModal from '../components/common/ChartConfigModal';
import ReportService from '../services/reportBuilder.service';

const { Title } = Typography;
const { Option } = Select;

const ReportViewer = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [chartConfig, setChartConfig] = useState(null);
  const [chartModalVisible, setChartModalVisible] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    // Load report from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const reportId = urlParams.get('id');
    const entityType = urlParams.get('entity');

    if (reportId && entityType) {
      loadReport(reportId, entityType);
    }
  }, []);

  const loadReport = async (reportId, entityType) => {
    try {
      setLoading(true);
      console.log('Loading report:', reportId, entityType);

      const reportResponse = await ReportService.getSavedSearch(entityType, reportId);
      console.log('Report response:', reportResponse);
      setReport(reportResponse);

      // Execute the report
      console.log('Executing saved search...');
      const result = await ReportService.executeSearch(reportResponse?.entityType, {criteria:reportResponse?.criteria});
      // const result = await ReportService.executeSavedSearch(entityType, reportId);
      console.log('Execute result:', result);

      if (result && result.data) {
        console.log('Setting report data:', result.data.length, 'records');
        setReportData(result.data);
      } else {
        console.log('No data in result');
        setReportData([]);
      }
    } catch (error) {
      console.error('Error loading report:', error);
      message.error('Failed to load report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    if (report) {
      await loadReport(report.id, report.entityType);
    }
  }, [report]);

  const handleExport = useCallback(async (format = 'csv') => {
    if (!report) return;

    try {
      setExporting(true);
      const response = await ReportService.exportReport(report.entityType, report.criteria, format);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report.name}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting report:', error);
      message.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  }, [report]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleShare = useCallback(() => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    message.success('Report URL copied to clipboard');
  }, []);

  const handleChartConfig = () => {
    setChartModalVisible(true);
  };

  const handleChartSave = (config) => {
    setChartConfig(config);
    setChartModalVisible(false);
  };

  const renderTableView = () => {
    if (!reportData || reportData.length === 0) {
      return (
        <Empty
          description="No data available"
          style={{ margin: '40px 0' }}
        />
      );
    }

    // Get columns from first row
    const columns = Object.keys(reportData[0]).map(key => ({
      title: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
      dataIndex: key,
      key: key,
      ellipsis: true,
      width: 150,
      sorter: (a, b) => {
        const aVal = a[key];
        const bVal = b[key];
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return aVal - bVal;
        }
        return String(aVal).localeCompare(String(bVal));
      }
    }));

    return (
      <Table
        dataSource={reportData}
        columns={columns}
        size="small"
        scroll={{ x: 'max-content', y: 400 }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
        }}
      />
    );
  };

  const renderChartView = () => {
    if (!chartConfig) {
      return (
        <div style={{
          height: '400px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          gap: '16px'
        }}>
          <div style={{ fontSize: '48px', color: '#d9d9d9' }}>
            📊
          </div>
          <Typography.Text type="secondary">
            Configure chart settings to visualize your data
          </Typography.Text>
          <Button
            type="primary"
            icon={<BarChartOutlined />}
            onClick={handleChartConfig}
          >
            Configure Chart
          </Button>
        </div>
      );
    }

    return (
      <div style={{ height: '500px', width: '100%' }}>
        <DataVisualization
          data={reportData}
          chartType={chartConfig.chartType}
          xAxisField={chartConfig.xAxisField}
          yAxisField={chartConfig.yAxisField}
          title={chartConfig.title}
          height={chartConfig.height || 400}
        />
      </div>
    );
  };

  if (!report) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div style={{ padding: '20px', background: '#f0f2f5', minHeight: '100vh' }}>
          <Card>
        {/* Header */}
        <Row justify="space-between" align="middle" style={{ marginBottom: '20px' }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>{report.name}</Title>
            <Typography.Text type="secondary">
              {report.entityType} • {reportData.length} records
            </Typography.Text>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
                Refresh
              </Button>
              <Button icon={<ShareAltOutlined />} onClick={handleShare}>
                Share
              </Button>
              <Select defaultValue="csv" style={{ width: 100 }}>
                <Option value="csv">CSV</Option>
                <Option value="excel">Excel</Option>
                <Option value="pdf">PDF</Option>
              </Select>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => handleExport('csv')}
                loading={exporting}
              >
                Export
              </Button>
              <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                Print
              </Button>
            </Space>
          </Col>
        </Row>

        {/* View Mode Selector */}
        <Row justify="space-between" align="middle" style={{ marginBottom: '20px' }}>
          <Col>
            <Space>
              <Button
                type={viewMode === 'table' ? 'primary' : 'default'}
                icon={<TableOutlined />}
                onClick={() => setViewMode('table')}
              >
                Table View
              </Button>
              <Button
                type={viewMode === 'chart' ? 'primary' : 'default'}
                icon={<BarChartOutlined />}
                onClick={() => setViewMode('chart')}
              >
                Chart View
              </Button>
            </Space>
          </Col>
          {viewMode === 'chart' && (
            <Col>
              <Button icon={<BarChartOutlined />} onClick={handleChartConfig}>
                Configure Chart
              </Button>
            </Col>
          )}
        </Row>

        {/* Content */}
        <Card bodyStyle={{ padding: '20px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <Spin size="large" />
            </div>
          ) : viewMode === 'table' ? renderTableView() : renderChartView()}
        </Card>
      </Card>

      <ChartConfigModal
        visible={chartModalVisible}
        onCancel={() => setChartModalVisible(false)}
        onSave={handleChartSave}
        data={reportData}
        initialConfig={chartConfig}
      />
        </div>
      </div>
    </div>
  );
};

export default ReportViewer;